/**
 * Fait : le contrat PUBLIC du catalogue de modèles — la liste blanche des
 *   fournisseurs (qui EST le critère 24 : Google, Gemini et Vertex n'y sont
 *   pas, donc n'entrent nulle part), les deux voies d'authentification
 *   (D-015), et les demandes d'administration : ajouter une entrée, la
 *   mettre à jour. Le formulaire du client et les routes de l'API valident
 *   avec les MÊMES schémas.
 *
 * Ne fait pas : il ne décrit pas le document en base (`donnees/economie.ts`
 *   le porte, et il importe ces constantes d'ici), ne fixe aucun prix — le
 *   catalogue est LA source, administrable et datée — et ne décide pas qui
 *   administre : `family_admin`, dit la fonction d'autorisation.
 */

import { z } from 'zod';

import { nomDonne } from './schemas.ts';

/**
 * Fournisseurs admis : OpenAI et Anthropic (D-004), Kimi Code par abonnement
 * (D-015), **OpenRouter** (D-017, ouvert par Charles le 2026-08-04) — un
 * intermédiaire unique, une seule clé, un seul relevé, et le choix du modèle
 * laissé au catalogue — et **Claude par abonnement** (`claude_code`, D-019,
 * 2026-08-16) : pas une clé ni un point d'entrée Anthropic, mais la session
 * locale du CLI officiel, jointe par le pont `apps/pont-claude`. Il n'a
 * AUCUNE voie payante déclarée : indisponible, il échoue en le disant.
 *
 * Cette liste reste FERMÉE par décision — le critère 24 se vérifie contre
 * elle, et `google`, `gemini`, `vertex` n'y sont toujours pas. **Ce que la
 * liste ne peut plus garantir seule** : OpenRouter donne accès, chez lui, à
 * des modèles de toutes origines. Le garde-fou se déplace donc d'un cran —
 * il n'est plus dans cette liste mais dans le CATALOGUE, où l'on inscrit
 * modèle par modèle ce que la famille peut appeler. Dit à Charles avant son
 * feu vert.
 */
export const FOURNISSEURS = [
  'openai',
  'anthropic',
  'kimi_code',
  'openrouter',
  'claude_code',
] as const;

export type Fournisseur = (typeof FOURNISSEURS)[number];

/** Comment on s'authentifie auprès d'un fournisseur (D-015). */
export const VOIES_D_AUTHENTIFICATION = ['cle_api', 'abonnement'] as const;

export type VoieDAuthentification = (typeof VOIES_D_AUTHENTIFICATION)[number];

/** Champs communs aux deux voies d'une demande d'ajout. */
const socleDeDemande = {
  fournisseur: z.enum(FOURNISSEURS),
  identifiantModele: z.string().min(1).max(200),
  libelle: nomDonne,
};

/**
 * Ajouter une entrée au catalogue. La voie « clé API » exige ses prix — en
 * CENTIMES par MILLION de jetons, entiers : l'argent ne se compte jamais en
 * flottants. La voie « abonnement » n'en accepte AUCUN : lui inventer un
 * prix laisserait croire qu'on peut l'additionner aux euros.
 */
export const demandeDEntreeDeCatalogue = z.discriminatedUnion('voie', [
  z
    .object({
      voie: z.literal('cle_api'),
      prixEntreeCentimesParMillionDeJetons: z.number().int().min(0),
      prixSortieCentimesParMillionDeJetons: z.number().int().min(0),
      ...socleDeDemande,
    })
    .strict(),
  z.object({ voie: z.literal('abonnement'), ...socleDeDemande }).strict(),
]);

export type DemandeDEntreeDeCatalogue = z.infer<typeof demandeDEntreeDeCatalogue>;

/**
 * Mettre à jour une entrée. La version porte l'arbitrage (critère 21) ; un
 * prix ne se change que sur la voie « clé API » — le SERVICE le refuse
 * ailleurs — et tout changement de prix re-date le relevé.
 */
export const demandeDeMiseAJourDeCatalogue = z
  .object({
    version: z.number().int().min(0),
    libelle: nomDonne.optional(),
    actif: z.boolean().optional(),
    prixEntreeCentimesParMillionDeJetons: z.number().int().min(0).optional(),
    prixSortieCentimesParMillionDeJetons: z.number().int().min(0).optional(),
  })
  .strict();

export type DemandeDeMiseAJourDeCatalogue = z.infer<typeof demandeDeMiseAJourDeCatalogue>;

/**
 * LES MODÈLES QU'ON PEUT DEMANDER À L'ABONNEMENT CLAUDE, avec leur nom lisible.
 *
 * POURQUOI CETTE LISTE EXISTE, écrite le 2026-08-26 : l'écran du catalogue
 * demandait un « identifiant technique » à taper à la main, et rien nulle part
 * ne disait lequel. Charles, essayant d'inscrire un modèle : « comment choisir
 * dans l'abonnement sonnet opus ou fable ». Un champ libre où trois valeurs
 * seulement sont acceptées n'est pas un champ, c'est une devinette.
 *
 * ELLE EST ICI ET NULLE PART AILLEURS. Le pont la lit pour décider ce qu'il
 * accepte de passer au CLI ; l'écran la lit pour proposer le choix. Deux
 * listes auraient divergé au premier modèle nouveau — et c'est exactement ce
 * qui venait d'arriver : le CLI accepte `fable`, le pont ne le connaissait
 * pas, donc `fable` était impossible à inscrire même en tapant le bon mot.
 *
 * LES CLÉS SONT LES ALIAS DU CLI, vérifiés dans `claude --help` le
 * 2026-08-26 : « Provide an alias for the latest model (e.g. 'fable', 'opus',
 * or 'sonnet') or a model's full name ». On s'en tient aux alias : ils suivent
 * le modèle le plus récent de leur famille sans qu'on ait à les rééditer.
 */
export const MODELES_DE_L_ABONNEMENT = {
  fable: 'Claude Fable — le plus capable, et le plus lent',
  opus: 'Claude Opus — puissant, pour les tâches difficiles',
  sonnet: 'Claude Sonnet — l’équilibre entre vitesse et finesse',
  haiku: 'Claude Haiku — le plus rapide, pour les tâches simples',
} as const;

export type ModeleDeLAbonnement = keyof typeof MODELES_DE_L_ABONNEMENT;

/** Les alias seuls — ce que le pont compare à la demande qu'il reçoit. */
export const ALIAS_DE_L_ABONNEMENT = Object.keys(
  MODELES_DE_L_ABONNEMENT,
) as readonly ModeleDeLAbonnement[];

/**
 * COMMENT ON SE CONNECTE À CHAQUE FOURNISSEUR — la voie se DÉDUIT, elle ne se
 * demande pas.
 *
 * Le formulaire du catalogue posait la question à l'écran, et c'était une
 * question à laquelle personne ne peut répondre sans connaître le montage :
 * Claude par abonnement n'a AUCUNE voie payante déclarée (D-019), le forfait
 * Kimi non plus, et les trois autres n'ont que la clé. Choisir la mauvaise
 * voie produisait une entrée de catalogue inutilisable, sans que rien ne le
 * dise avant le premier appel raté.
 *
 * Écrit le 2026-08-26, sur le retour de Charles : « je choisis par exemple
 * open routeur et c'est pas clair on va où on fait quoi comment ».
 *
 * Le jour où un fournisseur portera VRAIMENT deux voies, cette table devra
 * redevenir un choix — pour lui seul, pas pour tous.
 */
export const VOIE_DU_FOURNISSEUR: Readonly<Record<Fournisseur, VoieDAuthentification>> = {
  openai: 'cle_api',
  anthropic: 'cle_api',
  openrouter: 'cle_api',
  kimi_code: 'abonnement',
  claude_code: 'abonnement',
};

/** Le nom que l'on montre pour chaque fournisseur, et où trouver ses modèles. */
export const AIDE_DU_FOURNISSEUR: Readonly<
  Record<Fournisseur, { readonly nom: string; readonly ou: string }>
> = {
  openai: {
    nom: 'OpenAI',
    ou: 'L’identifiant se lit sur platform.openai.com, page « Models » — par exemple gpt-5.6.',
  },
  anthropic: {
    nom: 'Anthropic',
    ou: 'L’identifiant se lit sur platform.claude.com, page « Models » — par exemple claude-opus-5.',
  },
  openrouter: {
    nom: 'OpenRouter',
    ou:
      'L’identifiant se lit sur openrouter.ai/models : il porte toujours un fournisseur, une ' +
      'barre oblique et un modèle — par exemple anthropic/claude-sonnet-5.',
  },
  kimi_code: {
    nom: 'Kimi Code (votre forfait)',
    ou: 'L’identifiant se lit sur la console Kimi Code — par exemple kimi-k2.7.',
  },
  claude_code: {
    nom: 'Claude (votre abonnement)',
    ou: 'Choisissez dans la liste : ce sont les seuls que votre abonnement sait servir.',
  },
};
